import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  verifyBeforeUpdateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth'
import { doc, getDoc, setDoc, query, where, getDocs, collection, Timestamp, limit } from '@/services/neonFirestore'
import { auth, db } from '@/firebase/config'
import { apiGet, apiPost } from '@/services/apiClient'
import { clearLocalSession, getLocalSession, setLocalSession } from '@/services/localAuthSession'

import { ROLES, PERMISSIONS, hasPermission as checkPermission, isAtLeastManager } from '@/utils/rbac'
import { notificationService } from '@/services/notificationService'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const userProfile = ref(null)
  const loading = ref(false)
  const authInitialized = ref(false)
  const authLoading = ref(true)
  const profileLoading = ref(false)
  const customRoles = ref([])

  const isAuthenticated = computed(() => !!user.value)
  const isManager = computed(
    () => isAtLeastManager(userProfile.value?.role, customRoles.value),
  )
  const isSuperAdmin = computed(() => userProfile.value?.role === ROLES.SUPERADMIN)
  const hasPDPAConsent = computed(() => userProfile.value?.hasPDPAConsent || false)
  const hasFaceRegistered = computed(() => userProfile.value?.hasFaceRegistered || false)

  const can = (permission) => {
    if (!userProfile.value?.role) return false
    return checkPermission(userProfile.value.role, permission, customRoles.value)
  }

  const setCustomRoles = (roles) => {
    customRoles.value = roles
  }

  // Initialize auth state listener
  const initAuth = () => {
    if (authInitialized.value) return // Prevent multiple initializations

    authInitialized.value = true
    authLoading.value = true

    const restoreLocalLogin = async () => {
      const session = getLocalSession()
      if (!session?.accessToken || !session?.profile) return false

      // Render the persisted authenticated profile immediately. Firebase's
      // null auth-state callback can otherwise briefly clear the sidebar on a
      // Neon-local login before the profile refresh finishes.
      user.value = { uid: session.profile.id, email: session.profile.email, ...session.profile, isLocalAuth: true }
      userProfile.value = session.profile
      try {
        const { profile } = await apiGet('/v1/auth/local/me')
        user.value = { uid: profile.id, email: profile.email, ...profile, isLocalAuth: true }
        userProfile.value = profile
        setLocalSession({ ...session, profile })
        return true
      } catch (error) {
        // Only discard local state when the server proves the session is no
        // longer valid. A transient deployment/network failure must not make
        // an otherwise valid user's navigation disappear.
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          clearLocalSession()
          user.value = null
          userProfile.value = null
          return false
        }
        console.warn('Using cached Neon profile until session refresh succeeds:', error)
        return true
      }
    }

    // Timeout fallback for iOS/network issues - if Firebase doesn't respond in 10 seconds, 
    // stop loading to prevent infinite spinner
    const authTimeout = setTimeout(() => {
      if (authLoading.value) {
        console.warn('Auth initialization timeout - Firebase may be blocked or slow')
        authLoading.value = false
      }
    }, 10000)

    onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(authTimeout) // Clear timeout since Firebase responded
      if (firebaseUser) {
        clearLocalSession()
        user.value = firebaseUser
        await loadUserProfile(firebaseUser.uid)
        
        // Sync Firestore email with verified Auth email if they differ
        if (userProfile.value && userProfile.value.email !== firebaseUser.email) {
          try {
            await updateProfile({ email: firebaseUser.email })
          } catch (syncError) {
            console.error('Failed to sync email to Firestore:', syncError)
          }
        }
      } else {
        const restored = await restoreLocalLogin()
        if (!restored) {
          user.value = null
          userProfile.value = null
        }
      }
      authLoading.value = false
    })
  }

  // Load user profile from Neon API
  const loadUserProfile = async (uid) => {
    profileLoading.value = true
    try {
      const userDoc = await getDoc(doc(db, 'users', uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        userProfile.value = {
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          pdpaConsentDate: data.pdpaConsentDate?.toDate?.() || data.pdpaConsentDate,
        }

        // Request notification permission if authenticated (foreground listen is in App.vue)
        if (uid) {
          notificationService.requestPermission(uid).catch(err => {
            console.warn('Failed to initialize notifications:', err)
          })
        }
      } else {
        console.warn('User profile not found in Neon for uid:', uid)
        userProfile.value = null
      }
    } catch (error) {
      console.error('Error loading user profile:', error)
      // Surface API misconfig (wrong VITE_API_URL → 404 from face service / Netlify)
      if (error?.response?.status === 404 || error?.message?.includes('404')) {
        console.error(
          'Profile API 404: check Netlify VITE_API_URL points to the Node attendance-api (/health), not the face service.'
        )
      }
    } finally {
      profileLoading.value = false
    }
  }

  // Sign in with email and password
  const login = async (inputEmail, password) => {
    loading.value = true
    try {
      try {
        const session = await apiPost('/v1/auth/local/login', { email: inputEmail, password })
        setLocalSession(session)
        user.value = { uid: session.profile.id, email: session.profile.email, ...session.profile, isLocalAuth: true }
        userProfile.value = session.profile
        return user.value
      } catch (localError) {
        // A Firebase fallback must use Firebase's bearer token, not a stale
        // local session, when it bootstraps the Neon credential below.
        clearLocalSession()
        const userCredential = await signInWithEmailAndPassword(auth, inputEmail, password)
        try {
          await apiPost('/v1/auth/local/bootstrap-password', { password })
        } catch (bootstrapError) {
          console.warn('Firebase login succeeded but Neon password bootstrap failed:', bootstrapError)
        }
        return userCredential.user
      }
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Register new user
  const register = async (email, password, userData) => {
    loading.value = true
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      const newUser = userCredential.user

      // Create user profile in Firestore
      const newUserProfile = {
        uid: newUser.uid,
        email: newUser.email,
        name: userData.name || '',
        role: userData.role || ROLES.EMPLOYEE,
        companyId: userData.companyId || '',
        department: userData.department,
        position: userData.position,
        phoneNumber: userData.phoneNumber,
        hasPDPAConsent: false,
        hasFaceRegistered: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      }

      await setDoc(doc(db, 'users', newUser.uid), {
        ...newUserProfile,
        createdAt: newUserProfile.createdAt,
        updatedAt: newUserProfile.updatedAt,
      })

      return newUser
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Sign out
  const logout = async () => {
    try {
      clearLocalSession()
      if (auth.currentUser) await signOut(auth)
      user.value = null
      userProfile.value = null
    } catch (error) {
      throw error
    }
  }

  // Update user profile
  const updateProfile = async (updates) => {
    if (!user.value) return

    try {
      // Check if we need to migrate face descriptor from plain to encrypted
      if (updates.faceDescriptor && Array.isArray(updates.faceDescriptor)) {
        // Face descriptor is being updated as plain array - ensure it's encrypted
        try {
          const { ensureEncrypted } = await import('@/utils/security/biometricEncryption.js')
          updates.faceDescriptor = await ensureEncrypted(updates.faceDescriptor, user.value.uid)
        } catch (error) {
          console.error('Error encrypting face descriptor:', error)
          throw new Error('Failed to encrypt face descriptor: ' + error.message)
        }
      }

      const updatedProfile = {
        ...userProfile.value,
        ...updates,
        updatedAt: new Date(),
      }

      // Convert Date objects to Firestore Timestamps
      const firestoreData = { ...updatedProfile }

      // Convert updatedAt to Timestamp
      if (firestoreData.updatedAt instanceof Date) {
        firestoreData.updatedAt = Timestamp.fromDate(firestoreData.updatedAt)
      }

      // Convert createdAt to Timestamp if it exists and is a Date
      if (firestoreData.createdAt instanceof Date) {
        firestoreData.createdAt = Timestamp.fromDate(firestoreData.createdAt)
      }

      // Convert pdpaConsentDate to Timestamp if it exists and is a Date
      if (firestoreData.pdpaConsentDate instanceof Date) {
        firestoreData.pdpaConsentDate = Timestamp.fromDate(firestoreData.pdpaConsentDate)
      } else if (updates.pdpaConsentDate && !(updates.pdpaConsentDate instanceof Timestamp)) {
        // If pdpaConsentDate is passed in updates as a Date, convert it
        firestoreData.pdpaConsentDate = Timestamp.fromDate(updates.pdpaConsentDate)
      }

      await setDoc(doc(db, 'users', user.value.uid), firestoreData, { merge: true })

      // Reload profile to get updated data from Firestore
      await loadUserProfile(user.value.uid)
    } catch (error) {
      throw error
    }
  }

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    if (!user.value) throw new Error('User not authenticated')

    try {
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.value.email, currentPassword)
      await reauthenticateWithCredential(user.value, credential)

      // Update password
      await updatePassword(user.value, newPassword)
    } catch (error) {
      throw error
    }
  }

  // Change email - uses verifyBeforeUpdateEmail which is more secure
  const changeEmail = async (newEmail, currentPassword = null) => {
    if (!user.value) throw new Error('User not authenticated')

    try {
      // Re-authenticate user with current password if provided
      if (currentPassword) {
        const credential = EmailAuthProvider.credential(user.value.email, currentPassword)
        await reauthenticateWithCredential(user.value, credential)
      }

      // Use verifyBeforeUpdateEmail instead of updateEmail
      // This sends a verification link to the new email
      await verifyBeforeUpdateEmail(user.value, newEmail)

      // Note: We don't update Firestore email here because it only 
      // changes in Auth after the user clicks the verification link.
      // The Firestore update will happen when the user next logs in 
      // or we can handle it via a Cloud Function.
    } catch (error) {
      throw error
    }
  }

  const sendPasswordResetToEmployee = async ({ email }) => {
    if (!email) {
      throw new Error('Employee email is required')
    }

    await apiPost('/v1/auth/local/request-password-reset', { email })

    return [email]
  }

  // Send password reset email to the employee email only
  const sendPasswordResetLink = async (inputEmail) => {
    loading.value = true
    try {
      await apiPost('/v1/auth/local/request-password-reset', { email: inputEmail })
      return [inputEmail]
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Get decrypted face descriptor (for face matching when needed)
  const getDecryptedFaceDescriptor = async () => {
    if (!user.value || !userProfile.value?.faceDescriptor) {
      return null
    }

    try {
      const { ensureDecrypted } = await import('@/utils/security/biometricEncryption.js')
      const decryptedDescriptor = await ensureDecrypted(
        userProfile.value.faceDescriptor,
        user.value.uid,
      )
      return decryptedDescriptor
    } catch (error) {
      console.error('Error decrypting face descriptor:', error)
      throw error
    }
  }

  return {
    user,
    userProfile,
    loading,
    authInitialized,
    authLoading,
    profileLoading,
    isAuthenticated,
    isManager,
    isSuperAdmin,
    hasPDPAConsent,
    hasFaceRegistered,
    can,
    customRoles,
    setCustomRoles,
    initAuth,
    loadUserProfile,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    changeEmail,
    sendPasswordResetLink,
    sendPasswordResetToEmployee,
    getDecryptedFaceDescriptor,
  }
})
